<p-table
    stripedRows
    [value]="documents()"
    [filters]="filters"
    [loading]="loading()"
    [paginator]="totalCount() > tableRowDefault"
    [totalRecords]="totalCount()"
    [rows]="tableRowDefault"
    [rowsPerPageOptions]="tableRowsOptions"
    [paginatorLocale]="(locale$ | async)!"
    [showCurrentPageReport]="true"
    [currentPageReportTemplate]="tableCurrentPageReportTemplate()"
    [columns]="columns()"
    [resizableColumns]="true"
    [lazy]="true"
    (onLazyLoad)="loadItems($event)"
    paginatorDropdownAppendTo="body"
    columnResizeMode="expand"
    sortMode="multiple"
    [scrollable]="true"
    scrollHeight="75svh"
    class="@container/evidence-uploaded-files-table">
    <ng-template #header>
        <tr>
            @for (column of columns(); track column.field) {
                <th
                    [pSortableColumn]="column.field"
                    [pSortableColumnDisabled]="!column.sort"
                    pResizableColumn
                    [title]="column.label | translate"
                    scope="col"
                    pFrozenColumn
                    [frozen]="compactActions() && column.field === 'actions'"
                    alignFrozen="right">
                    <div class="p-table-sort-header">
                        {{ column.label | translate }}
                        @if (column.sort) {
                            <p-sortIcon
                                [field]="column.field"
                                class="flex items-center" />
                        }
                    </div>
                </th>
            }
        </tr>
        <tr>
            <th scope="col">
                <p-columnFilter
                    type="text"
                    field="id"
                    matchMode="equals"
                    [matchModeOptions]="equalsMatchModeOptions()"
                    class="[&_.p-inputtext]:max-w-[72px]"
                    display="row"
                    [attr.aria-label]="
                        'shared.evidence.table.columns.id' | translate
                    " />
            </th>
            <th scope="col">
                <p-columnFilter
                    type="text"
                    field="fileName"
                    matchMode="contains"
                    display="row"
                    [attr.aria-label]="
                        'shared.evidence.table.columns.fileName' | translate
                    " />
            </th>
            <th scope="col">
                <p-columnFilter
                    field="status"
                    matchMode="equals"
                    [matchModeOptions]="equalsMatchModeOptions()"
                    display="row">
                    <ng-template #filter let-value let-filter="filterCallback">
                        <p-select
                            filterBy="label"
                            [ngModel]="value"
                            [options]="evidenceStatuses"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            (onChange)="filter($event.value)"
                            [attr.aria-label]="
                                'shared.evidence.table.columns.status'
                                    | translate
                            ">
                            <ng-template #selectedItem let-selectedOption>
                                @if (selectedOption) {
                                    <p-tag
                                        [value]="
                                            selectedOption.label | translate
                                        "
                                        [severity]="
                                            selectedOption.primengTagSeverity
                                        " />
                                }
                            </ng-template>
                            <ng-template #item let-option>
                                <p-tag
                                    [value]="option.label | translate"
                                    [severity]="option.primengTagSeverity" />
                            </ng-template>
                        </p-select>
                    </ng-template>
                </p-columnFilter>
            </th>
            @if (showClientProfileId()) {
                <th scope="col">
                    <p-columnFilter
                        type="text"
                        field="clientProfileId"
                        matchMode="equals"
                        [matchModeOptions]="equalsMatchModeOptions()"
                        class="[&_.p-inputtext]:max-w-[72px]"
                        display="row"
                        [attr.aria-label]="
                            'shared.evidence.table.columns.clientProfileId'
                                | translate
                        " />
                </th>
            }
            <th
                pFrozenColumn
                [frozen]="compactActions()"
                alignFrozen="right"
                scope="col"></th>
        </tr>
    </ng-template>
    <ng-template #body let-document>
        <tr>
            <td [title]="document.id.toString()">
                {{ document.id }}
            </td>
            <td [title]="document.fileName" class="max-w-80 truncate">
                {{ document.fileName }}
            </td>
            @let statusLabel =
                getEvidenceStatusLabel(document.status) | translate;
            <td [title]="statusLabel">
                <p-tag
                    [value]="statusLabel"
                    [severity]="getEvidenceStatusSeverity(document.status)" />
            </td>
            @if (showClientProfileId()) {
                <td [title]="document.clientProfileId?.toString() ?? ''">
                    {{ document.clientProfileId }}
                </td>
            }
            <td pFrozenColumn [frozen]="compactActions()" alignFrozen="right">
                <div class="flex flex-wrap items-center gap-1">
                    <div
                        [class]="
                            compactActions()
                                ? 'flex items-center gap-1 @7xl/evidence-uploaded-files-table:hidden'
                                : 'flex items-center gap-1 @5xl/evidence-uploaded-files-table:hidden'
                        ">
                        @if (isPreviewable(document)) {
                            <button
                                mat-icon-button
                                color="accent"
                                type="button"
                                [disabled]="runningPreviews().has(document.id)"
                                [attr.aria-busy]="
                                    runningPreviews().has(document.id)
                                        ? true
                                        : null
                                "
                                [attr.aria-label]="
                                    'shared.buttons.preview' | translate
                                "
                                (click)="onPreview(document)">
                                <mat-icon>
                                    @if (runningPreviews().has(document.id)) {
                                        <apcam-loading-spinner size="xs" />
                                    } @else {
                                        remove_red_eye
                                    }
                                </mat-icon>
                            </button>
                        } @else {
                            <apcam-tooltip-custom
                                [customMatTooltip]="
                                    previewUnavailableLabelKey() | translate
                                ">
                                <button
                                    mat-icon-button
                                    color="accent"
                                    type="button"
                                    disabled
                                    disabledInteractive
                                    [attr.aria-label]="
                                        previewUnavailableLabelKey() | translate
                                    ">
                                    <mat-icon> remove_red_eye </mat-icon>
                                </button>
                            </apcam-tooltip-custom>
                        }
                        <button
                            mat-icon-button
                            color="primary"
                            type="button"
                            [disabled]="runningDownloads().has(document.id)"
                            [attr.aria-busy]="
                                runningDownloads().has(document.id)
                                    ? true
                                    : null
                            "
                            [attr.aria-label]="
                                'shared.buttons.download' | translate
                            "
                            (click)="onDownload(document)">
                            <mat-icon>
                                @if (runningDownloads().has(document.id)) {
                                    <apcam-loading-spinner size="xs" />
                                } @else {
                                    download
                                }
                            </mat-icon>
                        </button>
                    </div>
                    <div
                        [class]="
                            compactActions()
                                ? 'hidden flex-wrap gap-2 @7xl/evidence-uploaded-files-table:flex'
                                : 'hidden flex-wrap gap-2 @5xl/evidence-uploaded-files-table:flex'
                        ">
                        @if (isPreviewable(document)) {
                            <button
                                mat-stroked-button
                                color="accent"
                                type="button"
                                [disabled]="runningPreviews().has(document.id)"
                                [attr.aria-busy]="
                                    runningPreviews().has(document.id)
                                        ? true
                                        : null
                                "
                                (click)="onPreview(document)">
                                <mat-icon>
                                    @if (runningPreviews().has(document.id)) {
                                        <apcam-loading-spinner size="xs" />
                                    } @else {
                                        remove_red_eye
                                    }
                                </mat-icon>
                                {{ 'shared.buttons.preview' | translate }}
                            </button>
                        } @else {
                            <apcam-tooltip-custom
                                [customMatTooltip]="
                                    previewUnavailableLabelKey() | translate
                                ">
                                <button
                                    mat-stroked-button
                                    color="accent"
                                    type="button"
                                    disabled
                                    disabledInteractive
                                    [attr.aria-label]="
                                        previewUnavailableLabelKey() | translate
                                    ">
                                    <mat-icon> remove_red_eye </mat-icon>
                                    {{ 'shared.buttons.preview' | translate }}
                                </button>
                            </apcam-tooltip-custom>
                        }
                        <button
                            mat-flat-button
                            color="primary"
                            type="button"
                            [disabled]="runningDownloads().has(document.id)"
                            [attr.aria-busy]="
                                runningDownloads().has(document.id)
                                    ? true
                                    : null
                            "
                            (click)="onDownload(document)">
                            <mat-icon>
                                @if (runningDownloads().has(document.id)) {
                                    <apcam-loading-spinner size="xs" />
                                } @else {
                                    download
                                }
                            </mat-icon>
                            {{ 'shared.buttons.download' | translate }}
                        </button>
                    </div>
                </div>
            </td>
        </tr>
    </ng-template>
    <ng-template #emptymessage>
        <apcam-primeng-no-records
            class="table-row"
            [colSpan]="columns().length"
            [noLoadedData]="documents().length === 0" />
    </ng-template>
</p-table>
